/** Small shared pieces. Nothing here knows about the domain. */

import type { ReactNode } from 'react'
import { ApiError } from '../api'

export function Card({
  title,
  hint,
  actions,
  children,
  tight,
  wide,
}: {
  title?: ReactNode
  hint?: ReactNode
  actions?: ReactNode
  children: ReactNode
  tight?: boolean
  //: Let this card have the whole window. The reading measure the rest of the
  //: app keeps is right for prose and forms and wrong for a nine-column table,
  //: which answers it by stacking every cell into a one-word tower and pushing
  //: its own buttons out past the border.
  wide?: boolean
}) {
  return (
    <section className={wide ? 'card wide' : 'card'}>
      {(title || actions) && (
        <header>
          <h2>
            {title} {hint && <span className="hint">{hint}</span>}
          </h2>
          {actions}
        </header>
      )}
      <div className={tight ? 'body tight' : 'body'}>{children}</div>
    </section>
  )
}

export function Note({
  kind = 'info',
  children,
}: {
  kind?: 'info' | 'error' | 'warn' | 'ok'
  children: ReactNode
}) {
  return <div className={`note-box ${kind}`}>{children}</div>
}

export function Pill({ value }: { value: string }) {
  return <span className={`pill ${value}`}>{value.replace(/_/g, ' ')}</span>
}

export function Loading({ what = 'Loading' }: { what?: string }) {
  return <div className="empty">{what}…</div>
}

export function Empty({ children }: { children: ReactNode }) {
  return <div className="empty">{children}</div>
}

/**
 * Errors, phrased by what the reader can do about them.
 *
 * A 403 is not a failure to be retried — it means this role may not do this,
 * and saying "request failed" would send someone hunting for a bug that is
 * actually a deliberate control.
 */
export function ErrorNote({ error }: { error: unknown }) {
  if (!error) return null
  if (error instanceof ApiError && error.isForbidden) {
    return (
      <Note kind="warn">
        Your role does not have access to this. {error.message}
      </Note>
    )
  }
  const message = error instanceof Error ? error.message : String(error)
  return <Note kind="error">{message}</Note>
}

export function Field({
  label,
  children,
  caption,
}: {
  label: string
  caption?: string
  children: ReactNode
}) {
  return (
    <label>
      <span className="caption">
        {label}
        {caption && <span className="faint"> — {caption}</span>}
      </span>
      {children}
    </label>
  )
}

export function Stat({
  label,
  value,
  note,
}: {
  label: string
  value: ReactNode
  note?: ReactNode
}) {
  return (
    <div className="card stat">
      <div className="label">{label}</div>
      <div className="value">{value}</div>
      {note && <div className="note">{note}</div>}
    </div>
  )
}
